import { AppShell, Button, ConnectionBar, ConnectionDownBanner, Initials, type RailItem } from "@kitchen/ui";
import { useEffect, useState } from "react";
import { api, setToken, setUnauthorizedHandler } from "./api.js";
import { useHeartbeat } from "./heartbeat.js";
import { CustomerHome } from "./pages/customer/index.js";
import { Login } from "./pages/Login.js";
import { MenuPage, PricesPage } from "./pages/menu/index.js";
import { OrdersPage } from "./pages/orders/index.js";
import { KitchenPage } from "./pages/kitchen/index.js";
import { PhoneOrderPage } from "./pages/phone-order/index.js";
import { ProductionPage } from "./pages/production/index.js";
import { useScreenBeat } from "./pages/Screens.js";
import { OrganizationsPage } from "./pages/Organizations.js";
import { RolesPage } from "./pages/Roles.js";
import { SettingsPage, SitesPage } from "./pages/SitesSettings.js";
import { StaffPage } from "./pages/Staff.js";
import { InventoryPage } from "./pages/inventory/index.js";
import { DistributionPage } from "./pages/distribution/index.js";
import { DriverPage } from "./pages/driver/index.js";
import { FinancePage } from "./pages/finance/index.js";
import { HomePage } from "./pages/home/index.js";
import { ForecastPage } from "./pages/forecast/index.js";
import { ProfitPage } from "./pages/profit/index.js";
import { HrPage } from "./pages/hr/index.js";
import { DailyReportPage } from "./pages/daily-report/index.js";
import { MenuEngineeringPage } from "./pages/menu-engineering/index.js";
import { AccountingPage } from "./pages/accounting/index.js";
import { PurchasingPage } from "./pages/purchasing/index.js";
import { EventsPage } from "./pages/events/index.js";
import { type DeviceCreds, loadDevice, loadSession, saveSession, type SessionInfo } from "./session.js";

/** Tiny history router: the manager panel has few screens and needs no routing library yet. */
function usePath(): [string, (p: string) => void] {
  const [path, setPath] = useState(() => location.pathname);
  useEffect(() => {
    const on = () => {
      setPath(location.pathname);
    };
    addEventListener("popstate", on);
    return () => {
      removeEventListener("popstate", on);
    };
  }, []);
  return [
    path,
    (p) => {
      history.pushState(null, "", p);
      setPath(p);
    },
  ];
}

const SCREENS: Record<string, { title: string; render: () => React.ReactNode }> = {
  "/": { title: "پیشخوان", render: () => <Home /> },
  // F3 screens: each folder has one owner (docs/tasks/BOARD.md), so parallel work never edits this file.
  "/orders": { title: "سفارش‌ها", render: () => <OrdersPage /> },
  "/phone-order": { title: "ثبت تلفنی", render: () => <PhoneOrderPage /> },
  // F4 screens.
  "/production": { title: "تقسیم کار آشپزخانه‌ها", render: () => <ProductionPage /> },
  "/kitchen": { title: "تابلوی آشپزخانه", render: () => <KitchenPage /> },
  "/menu": { title: "منو", render: () => <MenuPage /> },
  "/prices": { title: "قیمت‌ها", render: () => <PricesPage /> },
  "/organizations": { title: "سازمان‌ها و واحدها", render: () => <OrganizationsPage /> },
  "/staff": { title: "کارکنان", render: () => <StaffPage /> },
  "/roles": { title: "نقش‌ها و دسترسی", render: () => <RolesPage /> },
  "/sites": { title: "آشپزخانه‌ها و ایستگاه‌ها", render: () => <SitesPage /> },
  "/settings": { title: "تنظیمات", render: () => <SettingsPage /> },
  "/inventory": { title: "انبار و موجودی", render: () => <InventoryPage /> },
  // F5 screens.
  "/distribution": { title: "توزیع و تحویل", render: () => <DistributionPage /> },
  "/driver": { title: "صفحه‌ی راننده", render: () => <DriverPage /> },
  // F7 screens.
  "/finance": { title: "مدیریت مالی و حسابداری", render: () => <FinancePage /> },
  "/forecast": { title: "پیش‌بینی و هوشمندی", render: () => <ForecastPage /> },
  "/profit": { title: "گزارش سود و زیان", render: () => <ProfitPage /> },
  "/hr": { title: "منابع انسانی و پرسنل", render: () => <HrPage /> },
  "/daily-report": { title: "گزارش روز", render: () => <DailyReportPage /> },
  "/menu-engineering": { title: "مهندسی منو", render: () => <MenuEngineeringPage /> },
  "/accounting": { title: "حسابداری دوبل و دفاتر", render: () => <AccountingPage /> },
  "/purchasing": { title: "خرید و تأمین‌کنندگان", render: () => <PurchasingPage /> },
  "/events": { title: "مراسم و تشریفات", render: () => <EventsPage /> },
};

/** Top pills = main modules (design v2 direction: dashboard, orders, kitchens, distribution, …). */
const NAV = [
  { label: "پیشخوان", href: "/" },
  { label: "سفارش‌ها", href: "/orders" },
  { label: "آشپزخانه‌ها", href: "/production" },
  { label: "انبار", href: "/inventory" },
  { label: "توزیع", href: "/distribution" },
  { label: "مالی", href: "/finance" },
  { label: "پیش‌بینی", href: "/forecast" },
  { label: "سود", href: "/profit" },
  { label: "پرسنل", href: "/hr" },
  { label: "گزارش روز", href: "/daily-report" },
  { label: "مهندسی منو", href: "/menu-engineering" },
  { label: "حسابداری", href: "/accounting" },
  { label: "خرید", href: "/purchasing" },
  { label: "مراسم", href: "/events" },
];

/** Side rail = shortcuts and setup screens. */
const RAIL: Omit<RailItem, "active">[] = [
  { label: "تابلوی آشپزخانه", icon: "flame", href: "/kitchen" },
  { label: "ثبت تلفنی", icon: "phone", href: "/phone-order" },
  { label: "منو", icon: "utensils", href: "/menu" },
  { label: "قیمت‌ها", icon: "wallet", href: "/prices" },
  { label: "انبار و موجودی", icon: "box", href: "/inventory" },
  { label: "سازمان‌ها و واحدها", icon: "building", href: "/organizations" },
  { label: "کارکنان", icon: "users", href: "/staff" },
  { label: "نقش‌ها و دسترسی", icon: "shield", href: "/roles" },
  { label: "آشپزخانه‌ها و ایستگاه‌ها", icon: "chef", href: "/sites" },
  { label: "تنظیمات", icon: "settings", href: "/settings" },
  { label: "توزیع", icon: "truck", href: "/distribution" },
  { label: "صفحه‌ی راننده", icon: "truck", href: "/driver" },
  { label: "مالی و فاکتورها", icon: "wallet", href: "/finance" },
  { label: "پیش‌بینی", icon: "chart", href: "/forecast" },
  { label: "سود", icon: "wallet", href: "/profit" },
  { label: "پرسنل", icon: "users", href: "/hr" },
  { label: "گزارش روز", icon: "printer", href: "/daily-report" },
  { label: "مهندسی منو", icon: "chart", href: "/menu-engineering" },
  { label: "حسابداری", icon: "wallet", href: "/accounting" },
  { label: "خرید و تأمین‌کنندگان", icon: "box", href: "/purchasing" },
  { label: "مراسم و تشریفات", icon: "calendar", href: "/events" },
];

export function App() {
  const [session, setSession] = useState<SessionInfo | null>(() => loadSession());
  const [device, setDevice] = useState<DeviceCreds | null>(() => loadDevice());
  const [path, go] = usePath();
  const hb = useHeartbeat();
  // An alert screen keeps beating while a staff session is open on it (F3-T11).
  useScreenBeat(session?.context === "staff");

  setToken(session?.token ?? null);
  useEffect(() => {
    // Idle lock: when the server ends the session, the PIN pad comes back (ADR-29 §11).
    setUnauthorizedHandler(() => {
      saveSession(null);
      setSession(null);
    });
    return () => {
      setUnauthorizedHandler(null);
    };
  }, []);

  if (!session || !device) {
    return (
      <Login
        onSignedIn={(s, d) => {
          saveSession(s);
          setToken(s.token);
          setDevice(d);
          setSession(s);
        }}
      />
    );
  }

  const lock = () => {
    void api("/auth/logout", { body: {} }).catch(() => undefined);
    saveSession(null);
    setSession(null);
  };

  if (session.context === "customer") return <CustomerHome name={device.displayName} onLock={lock} hb={hb} />;

  const screen = SCREENS[path] ?? SCREENS["/"];
  return (
    <AppShell
      title={screen?.title ?? ""}
      nav={NAV.map((n) => ({ ...n, active: path === n.href }))}
      rail={RAIL.map((r) => ({ ...r, active: path === r.href }))}
      onNavigate={go}
      tools={
        <>
          <ConnectionBar state={hb.state} lastOkAt={hb.lastOkAt} now={hb.now} />
          <Button variant="ghost" icon="lock" onClick={lock}>
            قفل
          </Button>
          <Initials text={device.displayName.slice(0, 2)} tone="b" />
        </>
      }
    >
      {hb.state === "down" && <ConnectionDownBanner onRetry={hb.retry} />}
      {screen?.render()}
    </AppShell>
  );
}

function Home() {
  return <HomePage />;
}
